import { parseIsoMs } from "@/features/opportunities/lib/format-relative-time";
import type { EventJobItem } from "@/features/events/api/events.api";
import { isEventProcessingTimedOut } from "./event-processing-display";

const MS_IN_SECOND = 1_000;
const MS_IN_MINUTE = 60_000;
const MS_IN_HOUR = 3_600_000;

export type EventTimingStageKey =
  | "clicked"
  | "received"
  | "published"
  | "started"
  | "completed";

export interface EventTimingStage {
  key: EventTimingStageKey;
  /** ISO timestamp, or null when the event never reached this stage. */
  at: string | null;
  /** Gap since the previous stage; null when either end of the gap is missing. */
  sincePreviousMs: number | null;
  /** The gap is still growing — the event is mid-flight. */
  isRunning: boolean;
  /** Mid-flight past the processing cap, so the gap is a floor rather than a fact. */
  isTimedOut: boolean;
}

/**
 * The stamps as a sequence rather than unrelated timestamps. The gaps are the
 * point: the click-to-request hop, the API's own pre-publish work, the wait in
 * the topic, and the run. Timing that starts at `publishedAt` hides the first
 * two, which is how a twenty-minute wait reads as a two-minute event.
 *
 * The two client-side stages are omitted rather than shown empty when the
 * event had no click behind it — a scheduler or a worker fan-out — because a
 * row of dashes reads as missing data rather than as "not applicable".
 */
export function getEventTimeline(
  event: EventJobItem,
  now: number,
  capMs: number,
): EventTimingStage[] {
  const clicked = parseIsoMs(event.clickedAt);
  const received = parseIsoMs(event.requestReceivedAt);
  const published = parseIsoMs(event.publishedAt);
  const started = parseIsoMs(event.processingStartedAt);
  const completed = parseIsoMs(event.processingCompletedAt);

  const isRunning = event.status === "PROCESSING" && started != null && completed == null;
  const isTimedOut = isEventProcessingTimedOut(event, now, capMs);

  const clientStages: EventTimingStage[] = [];
  if (event.clickedAt) {
    clientStages.push({
      key: "clicked",
      at: event.clickedAt,
      sincePreviousMs: null,
      isRunning: false,
      isTimedOut: false,
    });
  }
  if (event.requestReceivedAt) {
    clientStages.push({
      key: "received",
      at: event.requestReceivedAt,
      sincePreviousMs: gapMs(clicked, received),
      isRunning: false,
      isTimedOut: false,
    });
  }

  return [
    ...clientStages,
    {
      key: "published",
      at: event.publishedAt,
      // Against ingress where we have it, so the segment shown is the API's own
      // pre-publish work rather than a gap back to the click.
      sincePreviousMs: gapMs(received ?? clicked, published),
      isRunning: false,
      isTimedOut: false,
    },
    {
      key: "started",
      at: event.processingStartedAt,
      sincePreviousMs: gapMs(published, started),
      isRunning: false,
      isTimedOut: false,
    },
    {
      key: "completed",
      at: event.processingCompletedAt,
      sincePreviousMs: getRunMs({ event, started, completed, isRunning, isTimedOut, now, capMs }),
      isRunning,
      isTimedOut: isRunning && isTimedOut,
    },
  ];
}

/**
 * What the user waited for this row: its completion minus the click, falling
 * back to ingress and then to the publish. Null while the row is unfinished —
 * a wait that is still running has no total, and printing the elapsed time as
 * one is the exact misreading this column exists to correct.
 */
export function getEventEndToEndMs(event: EventJobItem): number | null {
  const start =
    parseIsoMs(event.clickedAt) ??
    parseIsoMs(event.requestReceivedAt) ??
    parseIsoMs(event.publishedAt);
  const end = parseIsoMs(event.processingCompletedAt);
  if (start == null || end == null) return null;
  return Math.max(0, end - start);
}

/** Sub-second at the top of the scale, because most events finish there. */
export function formatPreciseDurationMs(ms: number): string {
  const safe = Math.max(0, ms);
  if (safe < MS_IN_SECOND) return `${Math.round(safe)}ms`;
  if (safe < MS_IN_MINUTE) return `${(safe / MS_IN_SECOND).toFixed(1)}s`;
  if (safe < MS_IN_HOUR) {
    const minutes = Math.floor(safe / MS_IN_MINUTE);
    const seconds = Math.floor((safe % MS_IN_MINUTE) / MS_IN_SECOND);
    return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
  }
  const hours = Math.floor(safe / MS_IN_HOUR);
  const minutes = Math.floor((safe % MS_IN_HOUR) / MS_IN_MINUTE);
  return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
}

/**
 * One unit, never two, for chart axis ticks.
 *
 * The precise form above puts a space in anything over a minute ("1m 00s"), and
 * a space is the one thing a Recharts axis label can wrap on — so a latency axis
 * silently breaks "1m 00s" across two lines while "30.0s" stays put, which is
 * how the same axis ends up with labels of different heights. Dropping to a
 * single unit removes the space and roughly halves the widest label; the exact
 * figure still reaches the reader through the tooltip.
 */
export function formatCompactDurationMs(ms: number): string {
  const safe = Math.max(0, ms);
  if (safe < MS_IN_SECOND) return `${Math.round(safe)}ms`;
  if (safe < MS_IN_MINUTE) return `${Math.round(safe / MS_IN_SECOND)}s`;
  if (safe < MS_IN_HOUR) return `${trimTrailingZero(safe / MS_IN_MINUTE)}m`;
  return `${trimTrailingZero(safe / MS_IN_HOUR)}h`;
}

/** "1.5" keeps its decimal, "2.0" loses it — an axis tick has no room to spare. */
function trimTrailingZero(value: number): string {
  const fixed = value.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

/**
 * Clock time down to the millisecond. The drawer prints the date once above the
 * stages, so the rows only carry what differs between them.
 */
export function formatEventClockTime(value: string | null, locale?: string): string | null {
  const parsed = parseIsoMs(value);
  if (parsed == null) return null;
  return new Date(parsed).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    hour12: false,
  });
}

export function formatEventDate(value: string | null, locale?: string): string | null {
  const parsed = parseIsoMs(value);
  if (parsed == null) return null;
  return new Date(parsed).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function gapMs(from: number | null, to: number | null): number | null {
  if (from == null || to == null) return null;
  return Math.max(0, to - from);
}

function getRunMs({
  event,
  started,
  completed,
  isRunning,
  isTimedOut,
  now,
  capMs,
}: {
  event: EventJobItem;
  started: number | null;
  completed: number | null;
  isRunning: boolean;
  isTimedOut: boolean;
  now: number;
  capMs: number;
}): number | null {
  if (isRunning) {
    if (started == null) return null;
    // Past the cap the elapsed time says more about the missing sweep than
    // about the job, so it stops climbing.
    return isTimedOut ? capMs : Math.max(0, now - started);
  }
  // The backend figure wins where it exists: it was measured at the worker
  // rather than inferred from two timestamps written at different moments.
  if (event.runDurationMs != null) return Math.max(0, event.runDurationMs);
  return gapMs(started, completed);
}
